import classNames from "classnames";
import { ReactNode } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import {
  SKELETON_BASE_COLOR,
  SKELETON_HIGHLIGHT_COLOR,
} from "../../utils/constants";
import { EmptyState } from "../empty";
import Pagination from "../pagination";

export const TableComponent = ({
  headers = [],
  rows = [],
  className = "",
  isRowClickable = false,
  rowClassName = "",
  headerClassName = "",
  onRowClick = () => {},
  isTableEmpty = false,
  isLoading = false,
  emptyTitle = "Empty",
  emptyImage,
  emptySubTitle = "No data found",
  loadingRows = 5,
  loadingHeight = 48,
  ctaText = "",
  onCTAClick = () => {},
  ctaIcon = "",
  isPaginated = false,
  currentPage,
  totalCount,
  itemsPerPage,
  pageRangeDisplayed,
  onPageChange,
}: {
  headers: ReactNode[];
  rows: (
    | {
        id: string | number;
        items: ReactNode[];
      }
    | undefined
  )[];
  className?: string;
  isRowClickable?: boolean;
  rowClassName?: string;
  headerClassName?: string;
  onRowClick?: (clickedIndex: number) => void;
  isTableEmpty?: boolean;
  isLoading?: boolean;
  emptyTitle?: string;
  emptySubTitle?: string;
  emptyImage?: string;
  loadingRows?: number;
  loadingHeight?: number;
  ctaText?: string;
  onCTAClick?: () => void;
  ctaIcon?: string;
  isPaginated?: boolean;
  currentPage?: number;
  totalCount?: number;
  itemsPerPage?: number;
  pageRangeDisplayed?: number;
  onPageChange?: (page: number) => void;
}) => {
  return (
    <div className="relative overflow-x-scroll md:min-h-0 md:flex-1 md:overflow-y-auto">
      <table
        className={classNames(
          `min-w-full text-sm md:min-w-full`,
          "border-separate border-spacing-x-0 border-spacing-y-0",
          "rounded-2xl border border-grey-200",
          className,
        )}
      >
        {isLoading ? (
          <tbody>
            <tr>
              <td className="p-3">
                <Skeleton
                  count={loadingRows}
                  height={loadingHeight}
                  baseColor={SKELETON_BASE_COLOR}
                  highlightColor={SKELETON_HIGHLIGHT_COLOR}
                  className="my-1 rounded-xl"
                />
              </td>
            </tr>
          </tbody>
        ) : undefined}

        {isTableEmpty && !isLoading ? (
          <tbody>
            <tr>
              <td>
                <EmptyState
                  text={emptyTitle}
                  subtext={emptySubTitle}
                  textClassName="!text-[24px] !mt-2"
                  subtextClassName="!max-w-[330px]"
                  className="!gap-2"
                  ctaText={ctaText}
                  onCTAClick={onCTAClick}
                  ctaIcon={ctaIcon}
                  image={emptyImage}
                />
              </td>
            </tr>
          </tbody>
        ) : undefined}

        {!isTableEmpty && !isLoading ? (
          <>
            <thead className="sticky top-0 z-[1]">
              <tr>
                {headers?.map((header, idx) => {
                  const key = `table-header-${idx}`;
                  return header ? (
                    <th
                      key={key}
                      className={classNames(
                        "min-w-max border-b border-grey-200 bg-grey-50/90 p-3 text-left",
                        "first:rounded-tl-2xl last:rounded-tr-2xl",
                        headerClassName,
                      )}
                    >
                      {header}
                    </th>
                  ) : undefined;
                })}
              </tr>
            </thead>

            <tbody>
              {rows.map((row, idx) => {
                return row ? (
                  <tr
                    key={row?.id}
                    className={`group border-spacing-y-80${
                      isRowClickable &&
                      "transition-all duration-300 hover:cursor-pointer hover:bg-gray-50"
                    }`}
                    onClick={() => {
                      isRowClickable && onRowClick(idx);
                    }}
                  >
                    {row?.items?.map((item, idx) => {
                      const key = `row-item-${idx}`;
                      return item ? (
                        <td
                          key={key}
                          className={classNames(
                            `border-b border-grey-200 p-3 text-left transition-all duration-300`,
                            "group-last:border-b-0 group-last:first:rounded-bl-2xl group-last:last:rounded-br-2xl",
                            headers.length === 0 &&
                              "group-first:first:rounded-tl-2xl group-first:last:rounded-tr-2xl",
                            rowClassName,
                          )}
                        >
                          {item}
                        </td>
                      ) : undefined;
                    })}
                  </tr>
                ) : undefined;
              })}
            </tbody>
          </>
        ) : undefined}
      </table>

      {isPaginated && !isTableEmpty && (
        <div className="sticky bottom-0 left-0 z-[1] bg-white">
          <Pagination
            currentPage={currentPage!}
            totalCount={totalCount!}
            itemsPerPage={itemsPerPage!}
            onPageChange={onPageChange!}
            pageRangeDisplayed={pageRangeDisplayed}
            containerClassName="mt-3"
            showPagNumbers={false}
          />
        </div>
      )}
    </div>
  );
};
